import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Paperclip, Loader2, MessageSquareReply } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/Badges";
import { asList } from "@/lib/forms";

export const FEEDBACK_STATUS = {
  new: { label: "New", cls: "bg-lasso-tint text-lasso-navy" },
  in_review: { label: "In review", cls: "bg-amber-50 text-amber-800" },
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-700" },
};
export const FeedbackStatus = ({ status, testId }) => <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${FEEDBACK_STATUS[status]?.cls}`} data-testid={testId}>{FEEDBACK_STATUS[status]?.label}</span>;

export const Replies = ({ replies }) => replies.length > 0 && (
  <div className="mt-4 space-y-2">
    {replies.map((r, i) => (
      <div key={i} className="rounded-xl bg-lasso-paper p-3 text-sm" data-testid={`feedback-reply-${i}`}>
        <p className="flex items-center gap-1.5 text-xs font-semibold text-lasso-deep"><MessageSquareReply className="h-3.5 w-3.5" /> {r.by} · {formatDate(r.at)}</p>
        <p className="mt-1 whitespace-pre-line text-zinc-700">{r.note}</p>
      </div>
    ))}
  </div>
);

const EMPTY = { category: "", subject: "", message: "", priority: "normal", link_type: "none", link_id: "" };
const LINK_SOURCES = { sop: "/sops", course: "/courses", form: "/forms" };

function FeedbackForm({ categories, onSent }) {
  const [f, setF] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (f.link_type === "none") return setItems([]);
    api.get(LINK_SOURCES[f.link_type]).then((r) => setItems(asList(r.data).filter((x) => !x.status || x.status === "published")));
  }, [f.link_type]);

  const submit = async () => {
    if (!f.category) return toast.error("Choose a category");
    setSending(true);
    try {
      let attachment_file_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        attachment_file_id = (await api.post("/feedback/attachment", fd)).data.file_id;
      }
      const linked = f.link_type !== "none" && f.link_id;
      await api.post("/feedback", { category: f.category, subject: f.subject, message: f.message, priority: f.priority,
        link_type: linked ? f.link_type : null, link_id: linked ? f.link_id : null, attachment_file_id });
      toast.success("Thanks — your feedback was sent");
      setF(EMPTY);
      setFile(null);
      onSent();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4 rounded-2xl border border-lasso-line bg-white p-6" data-testid="feedback-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Category</Label>
          <Select value={f.category || undefined} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="feedback-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Priority</Label>
          <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
            <SelectTrigger data-testid="feedback-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2"><Label>Subject</Label><Input value={f.subject} onChange={(e) => set("subject", e.target.value)} data-testid="feedback-subject-input" /></div>
      <div className="space-y-2"><Label>Message</Label><Textarea rows={5} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="feedback-message-input" /></div>
      <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
        <div className="space-y-2"><Label>Related item</Label>
          <Select value={f.link_type} onValueChange={(v) => setF((x) => ({ ...x, link_type: v, link_id: "" }))}>
            <SelectTrigger data-testid="feedback-link-type-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="sop">SOP</SelectItem><SelectItem value="course">Course</SelectItem><SelectItem value="form">Form</SelectItem></SelectContent>
          </Select>
        </div>
        {f.link_type !== "none" && (
          <div className="space-y-2"><Label>Which one?</Label>
            <Select value={f.link_id || undefined} onValueChange={(v) => set("link_id", v)}>
              <SelectTrigger data-testid="feedback-link-item-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{items.map((x) => <SelectItem key={x.id} value={x.id}>{x.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-zinc-600 hover:text-lasso-ink">
          <Paperclip className="h-4 w-4" /> {file ? file.name : "Attach a file (optional)"}
          <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="feedback-attachment-input" />
        </label>
        <button onClick={submit} disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="feedback-submit-button">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send feedback
        </button>
      </div>
    </div>
  );
}

export default function Feedback() {
  const [meta, setMeta] = useState({ categories: [] });
  const [mine, setMine] = useState(null);
  const load = useCallback(() => api.get("/feedback/mine").then((r) => setMine(r.data)), []);
  useEffect(() => { api.get("/feedback/meta").then((r) => setMeta(r.data)); load(); }, [load]);

  return (
    <div className="fade-up">
      <PageHeader eyebrow="Feedback" title="Tell us what would help" description="Suggestions about training, SOPs, forms or the platform go straight to the Lasso Hub team. They'll reply by email." />
      <div className="grid gap-10 lg:grid-cols-[1fr_420px]">
        <FeedbackForm categories={meta.categories} onSent={load} />
        <section>
          <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.14em]">Your submissions</h2>
          {mine?.length === 0 && <EmptyState title="Nothing sent yet" testId="feedback-mine-empty" />}
          <div className="space-y-3" data-testid="feedback-mine-list">
            {mine?.map((fb) => (
              <div key={fb.id} className="rounded-2xl border border-lasso-line bg-white p-5" data-testid={`feedback-mine-${fb.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold">{fb.subject}</p>
                  <FeedbackStatus status={fb.status} testId={`feedback-mine-status-${fb.id}`} />
                </div>
                <p className="mt-1 text-xs text-zinc-500">{fb.category} · {formatDate(fb.created_at)}{fb.priority === "urgent" ? " · Urgent" : ""}</p>
                <Replies replies={fb.replies} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
